/**
 * Аналитика: конверт просмотров в фанов по моделям.
 *
 * Карточки — последний ПОЛНЫЙ отчётный день (10:00→10:00 Варшавы): просмотры
 * фермы по модели, фаны из отчётера, конверт «1 фан / X просмотров».
 * Клик по карточке — деталка с выбором дня или диапазона дат.
 * Фаны приезжают с отчётера сами в 10:15 (ретраи каждые 30 мин); кнопка
 * «Забрать отчёт» — ручной дозабор для админа.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { formatNum } from '../lib/format.js';

interface ModelRow {
  modelId: string;
  name: string;
  views: number;
  fans: number;
  /** Клики из прокладки (живые люди, без ботов); null — прокладки нет. */
  clicks: number | null;
}

interface ModelsResponse {
  date: string | null;
  models: ModelRow[];
  unmatched: { rawName: string; campaign: string; fans: number }[];
  unmatchedClicks: { handle: string; displayName: string; clicks: number }[];
}

interface CrmModel { id: string; name: string }

/** Строка непривязанной модели отчётера: дропдаун моделей срмки.
 *  Список моделей тянется заново при каждом открытии меню. */
function BindRow({ rawName, campaigns, fans, onBound }: {
  rawName: string;
  campaigns: number;
  fans: number;
  onBound: () => void;
}) {
  const [options, setOptions] = useState<CrmModel[] | null>(null);
  const [busy, setBusy] = useState(false);

  function loadOptions() {
    api<CrmModel[]>('/api/models').then((ms) => setOptions(ms.map((m) => ({ id: m.id, name: m.name }))))
      .catch(() => setOptions([]));
  }

  async function bind(modelId: string) {
    if (!modelId || busy) return;
    setBusy(true);
    try {
      await api('/api/analytics/map', { method: 'POST', body: JSON.stringify({ rawName, modelId }) });
      onBound();
    } finally {
      setBusy(false);
    }
  }

  async function ignore() {
    if (busy) return;
    setBusy(true);
    try {
      await api('/api/analytics/ignore', { method: 'POST', body: JSON.stringify({ rawName }) });
      onBound();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ minWidth: 220 }}>
        <b>«{rawName}»</b>
        <span style={{ color: 'var(--text-subtle)', marginLeft: 8, fontSize: 12 }}>
          {campaigns} камп. · {fans} фанов ждут привязки
        </span>
      </div>
      <select
        className="admin-input"
        style={{ width: 220 }}
        disabled={busy}
        defaultValue=""
        onMouseDown={() => { if (options == null) loadOptions(); }}
        onFocus={() => { if (options == null) loadOptions(); }}
        onChange={(e) => bind(e.target.value)}
      >
        <option value="">{busy ? 'Привязываю…' : '— выбрать модель срмки —'}</option>
        {(options ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <button
        className="row-open-btn"
        title="Не наша модель — убрать из списка"
        disabled={busy}
        style={{ color: 'var(--accent)' }}
        onClick={ignore}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

function fmtDay(d: string): string {
  const dt = new Date(d);
  const next = new Date(dt);
  next.setDate(next.getDate() + 1);
  const f = (x: Date) => x.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  return `${f(dt)}–${f(next)}`;
}

function Convert({ views, fans }: { views: number; fans: number }) {
  if (fans <= 0) return <span className="muted">нет фанов</span>;
  if (views <= 0) return <span className="muted">нет просмотров</span>;
  return <>1 фан / <b>{formatNum(Math.round(views / fans))}</b> просм.</>;
}

/** Строка привязки модели прокладки к модели срмки (клики). */
function ClickBindRow({ handle, displayName, clicks, onBound }: {
  handle: string;
  displayName: string;
  clicks: number;
  onBound: () => void;
}) {
  const [options, setOptions] = useState<CrmModel[] | null>(null);
  const [busy, setBusy] = useState(false);

  function loadOptions() {
    api<CrmModel[]>('/api/models')
      .then((ms) => setOptions(ms.map((m) => ({ id: m.id, name: m.name }))))
      .catch(() => setOptions([]));
  }

  async function bind(modelId: string | null) {
    if (busy) return;
    setBusy(true);
    try {
      await api('/api/analytics/click-map', {
        method: 'POST',
        body: JSON.stringify({ handle, modelId }),
      });
      onBound();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ minWidth: 220 }}>
        <b>{displayName}</b>
        <span style={{ color: 'var(--text-subtle)', marginLeft: 8, fontSize: 12 }}>
          @{handle} · {clicks} кликов ждут привязки
        </span>
      </div>
      <select
        className="admin-input"
        style={{ width: 220 }}
        disabled={busy}
        defaultValue=""
        onMouseDown={() => { if (options == null) loadOptions(); }}
        onFocus={() => { if (options == null) loadOptions(); }}
        onChange={(e) => bind(e.target.value || null)}
      >
        <option value="">{busy ? 'Привязываю…' : '— выбрать модель срмки —'}</option>
        {(options ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <button
        className="row-open-btn"
        title="Не наша модель — убрать из списка"
        disabled={busy}
        style={{ color: 'var(--accent)' }}
        onClick={() => bind(null)}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

export function AnalyticsPage({ me }: { me: User }) {
  const nav = useNavigate();
  const [data, setData] = useState<ModelsResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function load() {
    api<ModelsResponse>('/api/analytics/models').then(setData).catch(() => setData({ date: null, models: [], unmatched: [], unmatchedClicks: [] }));
  }
  useEffect(load, []);

  async function refresh() {
    setRefreshing(true);
    setErr(null);
    try {
      await api('/api/analytics/refresh', { method: 'POST' });
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Забор не удался');
    } finally {
      setRefreshing(false);
    }
  }

  if (!data) return <div className="empty-state">Загрузка…</div>;

  return (
    <div className="page-inner">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>Аналитика</h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            {data.date
              ? <>конверт за последний полный отчётный день <b>{fmtDay(data.date)}</b> (с 10 до 10 утра) · просмотры — акков «В работе» (и хьюманов, бывших в работе)</>
              : 'фан-отчётов ещё нет'}
          </div>
        </div>
        {me.role === 'admin' && (
          <button className="btn-secondary" onClick={refresh} disabled={refreshing}>
            {refreshing ? 'Забираю…' : 'Забрать отчёт сейчас'}
          </button>
        )}
      </div>

      {err && <div className="error" style={{ marginBottom: 12 }}>{err}</div>}

      {data.models.length === 0 ? (
        <div className="empty-state">Данных пока нет — фаны приедут с отчётера в 10:15.</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table clickable-rows">
              <thead>
                <tr>
                  <th>Модель</th>
                  <th>Просмотры</th>
                  <th>Клики</th>
                  <th>Фаны</th>
                  <th>Конверт</th>
                </tr>
              </thead>
              <tbody>
                {[...data.models]
                  .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
                  .map((m) => (
                    <tr
                      key={m.modelId}
                      onClick={() => nav(`/analytics/${m.modelId}`)}
                      title="Открыть конверт модели"
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <span className="model-avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{m.name[0]}</span>
                          <b>{m.name}</b>
                        </span>
                      </td>
                      <td>{formatNum(m.views)}</td>
                      <td>{m.clicks == null ? <span className="muted">—</span> : formatNum(m.clicks)}</td>
                      <td>{formatNum(m.fans)}</td>
                      <td><Convert views={m.views} fans={m.fans} /></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.unmatchedClicks.length > 0 && (
        <div className="card" style={{ marginTop: 18 }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            Модели прокладки — нужна привязка ({data.unmatchedClicks.length})
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginBottom: 8 }}>
            Клики по этим моделям приходят из прокладки, но не попадают в таблицу,
            пока не указано, какая это модель срмки. Привязка подтянет и прошлые дни.
          </div>
          {data.unmatchedClicks.map((c) => (
            <ClickBindRow key={c.handle} handle={c.handle} displayName={c.displayName} clicks={c.clicks} onBound={load} />
          ))}
        </div>
      )}

      {data.unmatched.length > 0 && (() => {
        // группируем кампании по имени модели отчётера
        const byName = new Map<string, { campaigns: number; fans: number }>();
        for (const u of data.unmatched) {
          const g = byName.get(u.rawName) ?? { campaigns: 0, fans: 0 };
          g.campaigns += 1;
          g.fans += u.fans;
          byName.set(u.rawName, g);
        }
        return (
          <div className="card" style={{ marginTop: 22 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>
              Новые модели отчётера — нужна привязка ({byName.size})
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginBottom: 8 }}>
              Фаны по этим кампаниям копятся, но в карточки не попадают, пока ты не укажешь,
              какая это модель срмки. Привязка задним числом подтянет уже накопленные отчёты.
            </div>
            {[...byName.entries()].sort((a, b) => b[1].fans - a[1].fans).map(([rawName, g]) => (
              <BindRow key={rawName} rawName={rawName} campaigns={g.campaigns} fans={g.fans} onBound={load} />
            ))}
          </div>
        );
      })()}

    </div>
  );
}
